import React from "react"
import { Link, graphql } from "gatsby"

import SEO from "../components/common/seo"
import Layout from "../components/common/layout"
import TopicStatsPageContent from "../components/topic/topicStatsPageContent"
import { computeTopicStats } from "../utils/topicStats"
import { buildReportContent } from "../utils/topicStatsReport"
import { topicSheetDateRange } from "../utils/topics"
import { getSchemaForTopicStats } from "../utils/schema"

const TopicStatsPage = ({ data, pageContext }) => {
  const topic = data.topic
  const communities = data.communities.nodes
  const platforms = data.allPlatforms.nodes.filter(
    platform => !platform.removed
  )
  const features = data.allFeatures.nodes
  const reviews = data.reviews.nodes
  const relatedTopics = data.relatedTopics.nodes

  const stats = computeTopicStats({
    topic,
    communities,
    platforms,
    features,
    reviews,
  })

  const { updatedDate } = topicSheetDateRange(topic)

  const report = buildReportContent({
    topic,
    stats,
    communities,
    platforms,
    relatedTopics,
    siteBenchmarks: pageContext.siteBenchmarks,
    updatedDate,
  })

  return (
    <Layout
      pageTitle={`${topic.name} Communities Report`}
      subtitle={
        <Link
          to={`/topics/${topic.slug}/`}
          className="text-base font-semibold text-gray-800 hover:text-rose-700 no-underline"
        >
          ← Back to {topic.name} communities
        </Link>
      }
      noBackground={true}
      enableShare={true}
    >
      <SEO
        title={report.meta.seoTitle}
        description={report.meta.metaDescription}
        imageUrl={topic.image}
        path={`/topics/${topic.slug}/stats/`}
        schema={getSchemaForTopicStats({
          topic,
          stats,
          report,
          updatedDate,
          description: report.meta.metaDescription,
        })}
      />

      <TopicStatsPageContent topic={topic} stats={stats} report={report} />
    </Layout>
  )
}

export const query = graphql`
  query TopicStatsPage(
    $slug: String!
    $communitySlugs: [String]!
    $relatedTopicSlugs: [String]!
  ) {
    topic: googleSpreadsheetSourceTopics(slug: { eq: $slug }) {
      slug
      name
      count
      people
      image
      description
      added
      updated
      views
      viewsWithCommunities
      visitors
      redirects
    }
    communities: allGoogleSpreadsheetSourceCommunities(
      filter: { slug: { in: $communitySlugs }, removed: { ne: true } }
      sort: { fields: order, order: ASC }
    ) {
      nodes {
        name
        slug
        members
        memberSize
        platform
        logo
        description
        promoted
        sponsored
        promotedTopics
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        courses
        tools
        token
        reviewCount
        reviewAvg
        established
        added
        views
        visitors
        redirects
      }
    }
    relatedTopics: allGoogleSpreadsheetSourceTopics(
      filter: { slug: { in: $relatedTopicSlugs } }
    ) {
      nodes {
        slug
        name
        count
      }
    }
    allPlatforms: allGoogleSpreadsheetSourcePlatforms(
      filter: { removed: { ne: true } }
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
        logoSmall
        removed
      }
    }
    allFeatures: allGoogleSpreadsheetSourceFeatures(
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
      }
    }
    reviews: allGoogleSpreadsheetSourceReviews(
      filter: { community: { in: $communitySlugs }, approved: { eq: true } }
    ) {
      nodes {
        community
        overall
      }
    }
  }
`

export default TopicStatsPage
